import React from 'react';

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Box, Link, Stack, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';

import type { TranslationCatalog } from './types';

interface Props {
  catalog: TranslationCatalog;
}

const livePulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(46, 125, 50, 0.45);
  }
  70% {
    box-shadow: 0 0 0 7px rgba(46, 125, 50, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(46, 125, 50, 0);
  }
`;

export default function LiveLockBanner({ catalog }: Props) {
  const lockedByUrl = catalog.lockedBy ? `${window.location.origin}/users/${catalog.lockedBy}` : undefined;

  return (
    <Box
      aria-label="Canvas live lock status"
      sx={{
        width: '100%',
        px: 1.25,
        py: 0.75,
        borderRadius: 0.75,
        bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
        border: 1,
        borderColor: (theme) => alpha(theme.palette.success.main, 0.24),
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap">
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'success.main',
            animation: `${livePulse} 1.8s infinite`,
            flexShrink: 0,
          }}
        />
        <Typography variant="subtitle2" fontWeight={700} color="success.dark">
          Locked canvas
        </Typography>
        <Typography variant="caption" color="text.primary" sx={{ flexBasis: { xs: '100%', lg: 'auto' } }}>
          These values are being used in a live canvas. Check with the person who locked it before unlocking; edits may
          affect active customer messaging.
        </Typography>
        {catalog.lockedBy && lockedByUrl && (
          <Link
            href={lockedByUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            aria-label={`View user ${catalog.lockedBy}`}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'success.dark' }}
          >
            <PersonOutlineIcon fontSize="inherit" color="success" />
            <Typography variant="caption" color="success.dark" fontWeight={700}>
              View profile
            </Typography>
          </Link>
        )}
      </Stack>
    </Box>
  );
}
